import { useMemo } from 'react';
import * as MLocales from '@mui/material/locale';
import * as DGLocales from '@mui/x-data-grid/locales';
import * as DPLocales from '@mui/x-date-pickers/locales';
import { createTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck, faSquare, faSquareMinus } from '@fortawesome/pro-light-svg-icons';

import { dataGridLangMap, languages } from 'constants/languages';

type SupportedMLocales = keyof typeof MLocales;
type SupportedDGLocales = keyof typeof DGLocales;
type SupportedDPLocales = keyof typeof DPLocales;

const useMuiTheme = () => {
  const { i18n } = useTranslation();

  const theme = useMemo(() => {
    const locale = dataGridLangMap[i18n.language as languages];

    return createTheme(
      {
        typography: {
          fontFamily: 'Neue Haas Grotesk',
          fontSize: 14.875, // (14 / 16) * expectedFontSizeInPx
          htmlFontSize: 17,
          button: {
            textTransform: 'none',
          },
        },
        components: {
          MuiListItemText: {
            styleOverrides: {
              root: {
                margin: 0,
              },
            },
          },
          MuiListItemIcon: {
            styleOverrides: {
              root: {
                minWidth: '30px',
              },
            },
          },
          MuiCheckbox: {
            defaultProps: {
              checkedIcon: <FontAwesomeIcon icon={faSquareCheck} />,
              icon: <FontAwesomeIcon icon={faSquare} />,
              indeterminateIcon: <FontAwesomeIcon icon={faSquareMinus} />,
            },
          },
          MuiButtonBase: {
            defaultProps: {
              disableRipple: true,
            },
          },
          MuiButton: {
            defaultProps: {
              sx: {
                ':active': {
                  boxShadow: 8,
                },
              },
            },
          },
        },
      },
      DGLocales[locale as SupportedDGLocales],
      DPLocales[locale as SupportedDPLocales],
      MLocales[locale as SupportedMLocales],
    );
  }, [i18n.language]);

  return theme;
};

export default useMuiTheme;
